import { isAxiosError } from 'axios'
import { DispatchHelpMessage, DispatchHelpSession, DispatchHelpSessionOwner, DispatchHelpTakeover } from '@numeo/types'
import { generalAgentServiceAxios } from 'setup/SetupAxios'

// Backend (general-agent-service) mounts these under /v1/dispatch-help with JWT auth;
// the cross-tenant support routes require the Dispatch Help operator allowlist.

interface Paginated<T> {
    success: boolean
    data: T
    pagination?: { total: number; page: number; limit: number }
}

interface ListSessionsParams {
    status?: 'all' | 'active'
    search?: string
    /** Keyset cursor — the previous page's `nextCursor`. The sessions endpoint has no `page` param:
     *  a cross-tenant OFFSET/COUNT would seq-scan the whole tab table on every load. */
    before?: string
    limit?: number
    /** Every session belonging to one owner — the "other conversations" panel. Server maps it onto the
     *  same `ownerIds` filter the phone search uses, so it costs no extra query. */
    ownerId?: string
}

/** Keyset page marker. `total` is deliberately absent server-side, so the list can only ever ask
 *  "is there more?" — never "how many". */
interface SessionsPageInfo {
    limit: number
    hasMore: boolean
    nextCursor: string | null
}

interface SessionsPage {
    sessions: DispatchHelpSession[]
    pagination: SessionsPageInfo
}

const EMPTY_PAGE_INFO: SessionsPageInfo = { limit: 0, hasMore: false, nextCursor: null }

export async function fetchSessions(params: ListSessionsParams = {}): Promise<SessionsPage> {
    const res = await generalAgentServiceAxios.get<{ success: boolean; data: DispatchHelpSession[]; pagination?: SessionsPageInfo }>(
        '/dispatch-help/sessions',
        { params }
    )
    return { sessions: res.data?.data ?? [], pagination: res.data?.pagination ?? EMPTY_PAGE_INFO }
}

/** One session by id — the list is a keyset window, so a session older than the first page (or filtered
 *  out of it) is reachable only this way. Used by the ?session=<tabId> deep link. */
export async function fetchSession(tabId: string): Promise<DispatchHelpSession | null> {
    const res = await generalAgentServiceAxios.get<{ success: boolean; data: DispatchHelpSession }>(`/dispatch-help/sessions/${tabId}`)
    return res.data?.data ?? null
}

/** Who is on the other end — account email, phone, connected mailboxes, virtual mailbox. Fetched when
 *  a thread opens; the list carries only the one-line label. Any field can be empty. */
export async function fetchSessionOwner(tabId: string): Promise<DispatchHelpSessionOwner | null> {
    const res = await generalAgentServiceAxios.get<{ success: boolean; data: DispatchHelpSessionOwner }>(`/dispatch-help/sessions/${tabId}/owner`)
    return res.data?.data ?? null
}

/** One page of a thread, NEWEST first server-side: page 1 is the latest `limit` messages, page 2 the
 *  `limit` before those. `total` is what tells the caller whether older pages exist. */
export async function fetchSessionMessages(tabId: string, page = 1, limit = 50): Promise<{ messages: DispatchHelpMessage[]; total: number }> {
    const res = await generalAgentServiceAxios.get<Paginated<DispatchHelpMessage[]>>(`/dispatch-help/sessions/${tabId}/messages`, { params: { page, limit } })
    const messages = res.data?.data ?? []
    return { messages, total: res.data?.pagination?.total ?? messages.length }
}

/** Uses the shared Bearer-token interceptor. Fetch only when the operator presses Play. */
export async function fetchSessionRecording(tabId: string, messageId: string, signal: AbortSignal): Promise<Blob> {
    const res = await generalAgentServiceAxios.get<Blob>(
        `/dispatch-help/sessions/${encodeURIComponent(tabId)}/messages/${encodeURIComponent(messageId)}/recording`,
        { responseType: 'blob', signal, timeout: 120_000 }
    )
    return res.data
}

export async function sendDispatcherMessage(tabId: string, text: string, dispatcherName?: string, invokeAi = false): Promise<DispatchHelpMessage> {
    // invokeAi (dispatcher tagged @numeoai) → backend routes the text to the AI as a prompt.
    const res = await generalAgentServiceAxios.post<Paginated<DispatchHelpMessage>>(`/dispatch-help/sessions/${tabId}/messages`, {
        text,
        dispatcher_name: dispatcherName,
        invoke_ai: invokeAi,
    })
    return res.data.data
}

export async function takeoverSession(tabId: string, dispatcherName?: string): Promise<DispatchHelpTakeover> {
    const res = await generalAgentServiceAxios.post<Paginated<DispatchHelpTakeover>>(`/dispatch-help/sessions/${tabId}/takeover`, {
        dispatcher_name: dispatcherName,
    })
    return res.data.data
}

export async function handbackSession(tabId: string): Promise<void> {
    await generalAgentServiceAxios.post(`/dispatch-help/sessions/${tabId}/handback`)
}

export type SessionsExportFormat = 'jsonl' | 'csv'

/** Server-side cap on the export range — mirror it client-side before firing the request. */
export const EXPORT_MAX_RANGE_DAYS = 31
// A month of transcripts takes a while server-side; without this the shared axios instance
// (no timeout) would leave a hung export spinning forever.
const EXPORT_TIMEOUT_MS = 120_000

/** Bulk session download for product review — JSONL (one session per line, full transcript) or a
 *  CSV summary. `from`/`to` are inclusive YYYY-MM-DD UTC days; the server caps the range at 31 days. */
export async function downloadSessionsExport(from: string, to: string, format: SessionsExportFormat): Promise<Blob> {
    const res = await generalAgentServiceAxios.get<Blob>('/dispatch-help/export', {
        params: { from, to, format },
        responseType: 'blob',
        timeout: EXPORT_TIMEOUT_MS,
    })
    return res.data
}

/** With `responseType: 'blob'` the server's JSON error body arrives as a Blob — decode it so the
 *  caller can show the real reason (range cap, access denied) instead of generic copy. */
export async function exportErrorMessage(error: unknown): Promise<string | null> {
    if (!isAxiosError(error) || !(error.response?.data instanceof Blob)) return null
    try {
        const parsed = JSON.parse(await error.response.data.text()) as { message?: string }
        return parsed.message ?? null
    } catch {
        return null
    }
}

/** Whether this admin is on the Gluon-managed Dispatch Help operator allowlist. Gates the sidebar
 *  item; the same call registers the admin as a fan-out target server-side (delivery is then gated on
 *  live socket presence — no heartbeat). Empty allowlist server-side → deny by default. */
export async function fetchDispatchHelpAccess(): Promise<boolean> {
    const res = await generalAgentServiceAxios.get<{ success: boolean; data: { allowed: boolean } }>('/dispatch-help/access')
    return res.data.data.allowed
}

// ── Emails (the session's broker threads, read + reply as the tab owner) ─────────────────────────

export interface SessionEmailThread {
    id: string // postingId (external_id) — needed to send
    rowNumber: number
    brokerName: string | null
    brokerEmail: string | null
    status: string | null // raw email_status (mapped to a badge)
    statusReason: string | null
    unreadCount: number
    emailThreadId: string
    originCity: string | null
    originState: string | null
    destinationCity: string | null
    destinationState: string | null
    latestBrokerRepliedAt: string | number | null
}

export interface SessionThreadEmail {
    messageId: string
    subject: string | null
    from: { email: string; name?: string }[]
    to: { email: string; name?: string }[]
    body: string | null
    cleanedBody: string | null
    emailDate: number | null
    sentBy: string | null // 'USER' | 'GENERAL_AGENT' | 'INITIAL_EMAIL' | null (broker when null/other)
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null)
const int = (v: unknown): number => (typeof v === 'number' ? v : Number(v) || 0)

function mapThread(r: Record<string, unknown>): SessionEmailThread {
    return {
        id: String(r.id ?? ''),
        rowNumber: int(r.row_number),
        brokerName: str(r.broker_name),
        brokerEmail: str(r.broker_email),
        status: str(r.status),
        statusReason: str(r.status_reason),
        unreadCount: int(r.unread_count),
        emailThreadId: String(r.email_thread_id ?? ''),
        originCity: str(r.origin_city),
        originState: str(r.origin_state),
        destinationCity: str(r.destination_city),
        destinationState: str(r.destination_state),
        latestBrokerRepliedAt: (r.latest_broker_replied_at as string | number | null) ?? null,
    }
}

/** The session's engaged broker email threads (owner's, cross-tenant, operator-gated server-side). */
export async function fetchSessionEmailThreads(tabId: string): Promise<SessionEmailThread[]> {
    const res = await generalAgentServiceAxios.get<{ success: boolean; data: { threads?: Record<string, unknown>[] } }>(
        `/dispatch-help/sessions/${tabId}/email-threads`
    )
    return (res.data.data?.threads ?? []).map(mapThread)
}

/** A thread's messages (broker + outbound), read from the owner's tenant. */
export async function fetchSessionThreadEmails(tabId: string, threadId: string): Promise<SessionThreadEmail[]> {
    const res = await generalAgentServiceAxios.get<{ success: boolean; data: SessionThreadEmail[] }>(
        `/dispatch-help/sessions/${tabId}/threads/${threadId}/emails`
    )
    return res.data.data ?? []
}

/** Send a broker reply AS the tab owner (from the owner's connected mailbox). */
export async function sendSessionEmail(tabId: string, postingId: string, body: string, subject?: string, recipientEmail?: string): Promise<void> {
    await generalAgentServiceAxios.post(`/dispatch-help/sessions/${tabId}/email`, { postingId, body, subject, recipientEmail })
}
